/**
 * Financing-spezifische API-Funktionen
 * Finanzierung, Darlehen und Zins-/Tilgungsplan
 */
import { api } from "./client";
import {
  FinancingResponse,
  FinancingWithLoans,
  FinancingUpdate,
  LoanWithPlan,
  LoanWithPlanResponse,
  LoanDeleteResponse,
  LoanCreate,
  LoanUpdate,
  AggregatedRepaymentPlanResponse,
  AuszahlungUpdate,
  AuszahlungUpdateResponse,
  SuggestedLoanAmount,
  AuszahlungenResponse,
  AuszahlungenBulkUpdate,
  SelectOption,
} from "../../types";

export const financingsApi = {
  // Options (Referenzdaten)

  /**
   * Holt die vordefinierten Darlehenstypen als Optionsliste (geschlossener Enum)
   */
  async getDarlehensTypOptions(): Promise<SelectOption[]> {
    return api.get("/financing/darlehens-typ-options");
  },

  /**
   * Holt die verfügbaren Auswahlwerte für den Finanzierungsstatus
   */
  async getFinanzierungStatusOptions(): Promise<{ options: string[] }> {
    return api.get("/financing/finanzierung-status-options");
  },

  // Financing (Finanzierung)

  /**
   * Holt die Finanzierung eines Projekts inkl. aller Darlehen und deren Tilgungspläne
   * Erstellt automatisch einen Eintrag mit Standardwerten, falls noch keiner existiert.
   */
  async getFinancing(projectId: string): Promise<FinancingWithLoans> {
    return api.get(`/projects/${projectId}/financing`);
  },

  /**
   * Aktualisiert die Finanzierung eines Projekts (nur Eigenkapitalquote)
   */
  async updateFinancing(
    projectId: string,
    data: FinancingUpdate,
  ): Promise<FinancingResponse> {
    return api.put(`/projects/${projectId}/financing`, data);
  },

  /**
   * Löscht die Finanzierung eines Projekts inkl. aller Darlehen und Tilgungspläne
   */
  async deleteFinancing(projectId: string): Promise<void> {
    return api.delete(`/projects/${projectId}/financing`);
  },

  // Suggested Loan Amount (Vorgeschlagene Darlehenshöhe)

  /**
   * Berechnet die vorgeschlagene Darlehenshöhe für ein neues Darlehen.
   *
   * Wird aufgerufen, um beim Öffnen des "Neues Darlehen"-Formulars
   * die Darlehenshöhe automatisch vorzufüllen.
   *
   * Logik:
   * - Erstes Darlehen: Fremdkapital (FK)
   * - Weitere Darlehen: max(0, FK - Summe bestehender Darlehen)
   */
  async getSuggestedLoanAmount(
    projectId: string,
  ): Promise<SuggestedLoanAmount> {
    return api.get(`/projects/${projectId}/financing/suggested-loan-amount`);
  },

  // Loans (Darlehen)

  async getLoans(projectId: string): Promise<LoanWithPlan[]> {
    return api.get(`/projects/${projectId}/financing/loans`);
  },

  async getLoan(projectId: string, loanId: string): Promise<LoanWithPlan> {
    return api.get(`/projects/${projectId}/financing/loans/${loanId}`);
  },

  /**
   * Erstellt ein neues Darlehen für ein Projekt
   * Der Tilgungsplan wird automatisch berechnet.
   */
  async createLoan(
    projectId: string,
    data: LoanCreate,
  ): Promise<LoanWithPlanResponse> {
    return api.post(`/projects/${projectId}/financing/loans`, data);
  },

  /**
   * Aktualisiert ein Darlehen
   * Der Tilgungsplan wird automatisch neu berechnet.
   */
  async updateLoan(
    projectId: string,
    loanId: string,
    data: LoanUpdate,
  ): Promise<LoanWithPlanResponse> {
    return api.put(`/projects/${projectId}/financing/loans/${loanId}`, data);
  },

  /**
   * Löscht ein Darlehen inkl. dessen Tilgungsplan
   */
  async deleteLoan(
    projectId: string,
    loanId: string,
  ): Promise<LoanDeleteResponse> {
    return api.delete(`/projects/${projectId}/financing/loans/${loanId}`);
  },

  // Aggregierter Tilgungsplan

  /**
   * Holt den aggregierten Zins- und Tilgungsplan für alle Darlehen eines Projekts
   *
   * Neues Format mit monatlicher Granularität:
   * - Aggregierte Jahresdaten mit monatlichen Details
   * - Auszahlungen pro Darlehen und Jahr
   * - Zinsen-Details (Kreditzinsen + Bereitstellungszinsen)
   * - Validierungsfehler für Auszahlungen
   */
  async getAggregatedRepaymentPlan(
    projectId: string,
  ): Promise<AggregatedRepaymentPlanResponse> {
    return api.get(
      `/projects/${projectId}/financing/repayment-plan/aggregated`,
    );
  },

  // Auszahlungen

  async updateAuszahlung(
    projectId: string,
    loanId: string,
    data: AuszahlungUpdate,
  ): Promise<AuszahlungUpdateResponse> {
    return api.patch(
      `/projects/${projectId}/financing/loans/${loanId}/auszahlung`,
      data,
    );
  },

  // Auszahlungsplan (Detail-View)

  /**
   * Holt die Auszahlungspunkte eines Darlehens für die Detail-View.
   * Gibt nur Monate mit Auszahlung > 0 zurück.
   */
  async getAuszahlungen(
    projectId: string,
    loanId: string,
  ): Promise<AuszahlungenResponse> {
    return api.get(
      `/projects/${projectId}/financing/loans/${loanId}/auszahlungen`,
    );
  },

  /**
   * Aktualisiert alle Auszahlungen eines Darlehens (Bulk-Update).
   * Die Hauptauszahlung wird automatisch als Restbetrag berechnet.
   */
  async updateAuszahlungen(
    projectId: string,
    loanId: string,
    data: AuszahlungenBulkUpdate,
  ): Promise<AuszahlungenResponse> {
    return api.put(
      `/projects/${projectId}/financing/loans/${loanId}/auszahlungen`,
      data,
    );
  },
};
